/**
 * Fixed-price subscriptions, and what a project actually does to their quota.
 *
 * The rest of this engine prices a project at the API grid: measured tokens times a
 * published rate. That is right for whoever pays per use, and wrong for whoever takes a
 * subscription — there, the marginal cost of a token is nil up to the ceiling, and the
 * question stops being "which model is cheapest" and becomes "which is the best model whose
 * plan I can afford, and does my project fit in it".
 *
 * Two rules carried over from `capability.ts`, for the same reason:
 *
 * 1. **A capacity is never invented.** Vendors publish ceilings in opaque units — messages,
 *    percentages, sliding windows — and converting those to tokens is guesswork. A capacity
 *    carries one of three bases, never a fourth:
 *
 *      'measured'  we ran the bench and read the plan's own counter (SuperGrok Lite, and
 *                  ChatGPT Plus for GPT-5.6 Sol only)
 *      'published' the vendor publishes the ceiling AND the arithmetic to convert it (Z.ai,
 *                  alone of the nine: it gives credits per week and the per-model formula)
 *      'none'      the ceiling is unpublished or unconvertible — the interface says so
 *
 *    A 'published' ceiling is not weaker than a measured one, it is differently sourced: it
 *    comes from the vendor rather than from us, and it holds per model rather than per plan,
 *    because the multipliers differ by model.
 * 2. **Everything is dated.** There is no API for subscription prices; they come from a
 *    fetch of the vendor's own pricing page, and they move. `fetched_fr` says when.
 */

/** Which ceiling a measured capacity refers to. Vendors use both, and the difference matters:
 *  a weekly bucket refills four times a month, a monthly one does not. */
export type CapacityPeriod = 'week' | 'month'

export interface PlanCapacity {
  /** Apparent tokens the plan allows per `period`. Only ever set for 'measured': a
   *  'published' ceiling in credits converts differently for each model, so a single token
   *  figure would be wrong for all but one of them. */
  apparent_tokens: number | null
  period: CapacityPeriod | null
  basis: 'measured' | 'published' | 'none'
  /** Set for 'published': the ceiling in the vendor's own unit, plus the arithmetic. */
  credits?: {
    per_period: number
    /** modelId → [input, cached input, output] multipliers, as the vendor publishes them. */
    multipliers: Record<string, [number, number, number]>
    /** The vendor's own divisor in `credits = (tokens x multipliers) / divisor`. */
    divisor: number
  }
  /** What establishes it — a run of ours, or the page that publishes it. Auditable either way. */
  source_fr: string | null
  /** For 'measured': the models the reading holds for. One envelope can serve several models
   *  that do not spend it at the same rate, so a ceiling read on one says nothing about the
   *  others. Absent means every model the plan serves. */
  measured_for?: string[]
}

export interface Plan {
  id: string
  vendor_fr: string
  name_fr: string
  usd_per_month: number
  /**
   * Le prix que l'éditeur affiche EN EUROS, quand il en affiche un. Relevé le 21/09/2026 :
   * sur six éditeurs, deux seulement publient des euros — OpenAI et Anthropic. Les autres
   * affichent des dollars même vus de France, xAI écrivant jusqu'à « $10 USD/month ».
   *
   * Le comparateur continue de classer les forfaits en dollars, pour comparer des choses
   * comparables ; mais le chiffre MONTRÉ à un lecteur français est celui qu'il paiera. Sans
   * ce champ, ChatGPT Go s'affichait à 7,36 € — la conversion de ses 8 $ — quand la page
   * d'OpenAI dit 8 €.
   */
  eur_per_month?: number
  /** Catalogue model ids this plan gives access to. */
  models: string[]
  /** The vendor's own coding tool, when the plan includes it. Access to a chat window does
   *  not build a project; access to a CLI or an IDE does. */
  coding_tool_fr: string | null
  url: string
  fetched_fr: string
  /** The ceiling AS THE VENDOR PUBLISHES IT, in its own unit. Not converted. */
  published_limits_fr: string | null
  capacity: PlanCapacity
  caveat_fr: string
}

const NO_CAPACITY: PlanCapacity = {
  apparent_tokens: null, period: null, basis: 'none', source_fr: null,
}

/**
 * The plans we have confirmed. Deliberately short: a plan belongs here once its price comes
 * from the vendor's page, not from memory. `tools/fetch_subscriptions.py` fills it.
 */
export const PLANS: Plan[] = [
  // ——— xAI ———————————————————————————————————————————————————————————————
  {
    id: 'xai/supergrok-lite',
    vendor_fr: 'xAI', name_fr: 'SuperGrok Lite', usd_per_month: 10,
    models: ['x-ai/grok-4.6'],
    coding_tool_fr: 'Grok Build (CLI)',
    url: 'https://x.ai/pricing',
    fetched_fr: 'relevé le 14/09/2026',
    published_limits_fr: null,
    capacity: {
      apparent_tokens: 33_586_619, period: 'week', basis: 'measured',
      source_fr: 'run Grok 4.6 du 14/09/2026 : neuf tâches pour 27,2 M tokens apparents relevées '
               + 'à 81 % du quota, puis plafond hebdomadaire atteint à 31,8 M cumulés',
    },
    caveat_fr: 'Le prix de 10 $ ne figure pas sur x.ai/pricing, qui affiche la colonne Lite sans '
             + 'tarif : il est relevé sur la fiche App Store de xAI. La capacité, elle, est '
             + 'mesurée sur un run réel.',
  },
  {
    id: 'xai/supergrok',
    vendor_fr: 'xAI', name_fr: 'SuperGrok', usd_per_month: 30,
    models: ['x-ai/grok-4.6'],
    coding_tool_fr: 'Grok Build (CLI)',
    url: 'https://x.ai/pricing',
    fetched_fr: 'relevé le 14/09/2026',
    published_limits_fr: null,
    capacity: NO_CAPACITY,
    caveat_fr: 'xAI ne publie aucun plafond chiffré.',
  },
  {
    id: 'xai/supergrok-plus',
    vendor_fr: 'xAI', name_fr: 'SuperGrok Plus', usd_per_month: 100,
    models: ['x-ai/grok-4.6'],
    coding_tool_fr: 'Grok Build (CLI)',
    url: 'https://x.ai/pricing',
    fetched_fr: 'relevé le 14/09/2026',
    published_limits_fr: null,
    capacity: NO_CAPACITY,
    caveat_fr: 'xAI ne publie aucun plafond chiffré.',
  },

  // ——— Z.ai — le seul éditeur qui publie la conversion de son unité vers les tokens ———
  {
    id: 'zai/coding-lite',
    vendor_fr: 'Z.ai', name_fr: 'GLM Coding Plan — Lite', usd_per_month: 18,
    models: ['z-ai/glm-5.3', 'z-ai/glm-5.3-flash'],
    coding_tool_fr: 'ZCode, Claude Code et 20+ outils',
    url: 'https://z.ai/subscribe',
    fetched_fr: 'relevé le 14/09/2026',
    published_limits_fr: '10 000 Credits par semaine',
    capacity: {
      apparent_tokens: null, period: 'week', basis: 'published',
      credits: {
        per_period: 10000,
        // docs.z.ai/devpack/overview, « Credit Calculation » : credits =
        // (in x mult_in + cached x mult_cached + out x mult_out) / 10 000
        multipliers: {
          'z-ai/glm-5.3': [6.9, 1.7, 24],
          'z-ai/glm-5.3-flash': [2.3, 0.56, 8],
        },
        divisor: 10_000,
      },
      source_fr: 'plafond publié en Credits, convertible par la formule officielle de l’éditeur : '
               + 'nos neuf tâches valent 6 645 Credits sur GLM-5.3-Flash (66 % d’une semaine) et '
               + '14 820 sur GLM-5.3 (148 % : une semaine ne suffit pas, il faut étaler sur deux)',
    },
    caveat_fr: 'Seul éditeur du tableau à publier la conversion de son unité vers les tokens, ce '
             + 'qui permet de chiffrer ses plafonds sans mesure. Attention : la page sort par '
             + 'défaut sur l’annuel remisé (12,60 $) — le tarif mensuel est bien 18 $.',
  },
  {
    id: 'zai/coding-pro',
    vendor_fr: 'Z.ai', name_fr: 'GLM Coding Plan — Pro', usd_per_month: 80,
    models: ['z-ai/glm-5.3', 'z-ai/glm-5.3-flash'],
    coding_tool_fr: 'ZCode, Claude Code et 20+ outils',
    url: 'https://z.ai/subscribe',
    fetched_fr: 'relevé le 14/09/2026',
    published_limits_fr: '60 000 Credits par semaine',
    capacity: {
      apparent_tokens: null, period: 'week', basis: 'published',
      credits: {
        per_period: 60000,
        // docs.z.ai/devpack/overview, « Credit Calculation » : credits =
        // (in x mult_in + cached x mult_cached + out x mult_out) / 10 000
        multipliers: {
          'z-ai/glm-5.3': [6.9, 1.7, 24],
          'z-ai/glm-5.3-flash': [2.3, 0.56, 8],
        },
        divisor: 10_000,
      },
      source_fr: 'plafond publié en Credits, converti par la formule officielle : nos neuf '
               + 'tâches valent 11 % d’une semaine sur GLM-5.3-Flash et 25 % sur GLM-5.3',
    },
    caveat_fr: 'Tarif mensuel ; la page affiche l’annuel remisé par défaut.',
  },

  // ——— Anthropic ————————————————————————————————————————————————————————
  {
    id: 'anthropic/pro',
    vendor_fr: 'Anthropic', name_fr: 'Claude Pro', usd_per_month: 20,
    models: ['anthropic/claude-opus-5', 'anthropic/claude-opus-4.6',
             'anthropic/claude-sonnet-5', 'anthropic/claude-haiku-4.5'],
    coding_tool_fr: 'Claude Code (CLI)',
    url: 'https://claude.com/pricing',
    fetched_fr: 'relevé le 14/09/2026',
    published_limits_fr: 'fenêtre glissante de 5 h, plus des plafonds hebdomadaires non chiffrés ; '
                       + '« au moins 5× l’usage du plan gratuit », lui-même non chiffré',
    capacity: NO_CAPACITY,
    caveat_fr: 'Les plans nomment des FAMILLES (« Opus », « Sonnet »), jamais des versions : le '
             + 'rattachement aux versions que nous chiffrons est le nôtre. Anthropic écrit qu’il '
             + 'n’existe « aucun nombre de messages fixe » et se réserve de bouger les plafonds. '
             + 'Claude Fable n’est pas inclus dans les plafonds de Pro (crédits à l’usage).',
  },
  {
    id: 'anthropic/max-5x',
    vendor_fr: 'Anthropic', name_fr: 'Claude Max 5×', usd_per_month: 100, eur_per_month: 90,
    models: ['anthropic/claude-opus-5', 'anthropic/claude-opus-4.6',
             'anthropic/claude-sonnet-5', 'anthropic/claude-haiku-4.5', 'anthropic/claude-fable-5.1'],
    coding_tool_fr: 'Claude Code (CLI)',
    url: 'https://claude.com/claude-code',
    fetched_fr: 'relevé le 14/09/2026',
    published_limits_fr: '5× l’usage de Pro par session de 5 h ; Fable à 50 % des plafonds hebdomadaires',
    capacity: NO_CAPACITY,
    caveat_fr: 'Le prix par palier n’est pas sur la page tarifaire, qui n’affiche que « From $100 » : '
             + 'il est relevé sur claude.com/claude-code. Familles et non versions.',
  },
  {
    id: 'anthropic/max-20x',
    vendor_fr: 'Anthropic', name_fr: 'Claude Max 20×', usd_per_month: 200, eur_per_month: 180,
    models: ['anthropic/claude-opus-5', 'anthropic/claude-opus-4.6',
             'anthropic/claude-sonnet-5', 'anthropic/claude-haiku-4.5', 'anthropic/claude-fable-5.1'],
    coding_tool_fr: 'Claude Code (CLI)',
    url: 'https://claude.com/claude-code',
    fetched_fr: 'relevé le 14/09/2026',
    published_limits_fr: '20× l’usage de Pro par session de 5 h',
    capacity: NO_CAPACITY,
    caveat_fr: 'Prix relevé sur claude.com/claude-code. Familles et non versions.',
  },

  // ——— OpenAI ———————————————————————————————————————————————————————————
  {
    id: 'openai/go',
    vendor_fr: 'OpenAI', name_fr: 'ChatGPT Go', usd_per_month: 8, eur_per_month: 8,
    models: ['openai/gpt-5.6-terra'],
    coding_tool_fr: 'Codex, même enveloppe d’usage que le chat',
    url: 'https://learn.chatgpt.com/docs/pricing',
    fetched_fr: 'relevé le 14/09/2026',
    published_limits_fr: 'plafonds exprimés en « messages par période de 5 h », donnés en '
                       + 'fourchettes et explicitement non contractuels',
    capacity: NO_CAPACITY,
    caveat_fr: 'La page n’énumère pas les modèles de ce palier ; le rattachement est prudent.',
  },
  {
    id: 'openai/plus',
    vendor_fr: 'OpenAI', name_fr: 'ChatGPT Plus', usd_per_month: 20, eur_per_month: 23,
    models: ['openai/gpt-6-astra', 'openai/gpt-5.6-sol', 'openai/gpt-5.6-terra'],
    coding_tool_fr: 'Codex, même enveloppe d’usage que le chat',
    url: 'https://learn.chatgpt.com/docs/pricing',
    fetched_fr: 'relevé le 14/09/2026',
    published_limits_fr: 'par période de 5 h : Astra 5 à 45 messages, Sol 10 à 100, Terra 25 à 200 — '
                       + '« ces estimations ne sont pas des plafonds fixes »',
    // Read off Codex's own counters, written into every rollout: 30.8M Sol tokens moved the
    // weekly counter from 79 to 100 on 15/09 (data/bench-sol.json, quota_abonnement). The
    // honest range on that token mix is 140-160M; a Sol run on 14/09 read 182-197M.
    capacity: {
      apparent_tokens: 146_000_000, period: 'week', basis: 'measured',
      measured_for: ['openai/gpt-5.6-sol'],
      source_fr: 'compteurs de Codex relevés sur nos deux runs GPT-5.6 Sol du 15/09/2026 : 30,8 M '
               + 'de jetons pour 21 points du quota hebdomadaire, soit environ 146 M par semaine '
               + '(140 à 200 M selon le jour et le mélange de jetons), et environ 22,5 M par '
               + 'fenêtre de 5 h. Valable pour Sol seulement',
    },
    caveat_fr: 'Deux plafonds imbriqués, 5 h et 7 jours, et les deux ont coupé nos runs : la '
             + 'fenêtre de 5 h le 14/09, la semaine le 15/09, en pleine vérification finale d’un '
             + 'site. Une tâche de code d’environ 15 M de jetons occupe les deux tiers d’une '
             + 'fenêtre de 5 h et un dixième de la semaine. Le quota ne compte vraisemblablement '
             + 'pas tous les jetons au même poids, et la mesure ne vaut que pour Sol. Facturé '
             + '23 € depuis la France : ce n’est pas un taux de change, c’est une grille régionale.',
  },
  {
    id: 'openai/pro-5x',
    vendor_fr: 'OpenAI', name_fr: 'ChatGPT Pro 5×', usd_per_month: 100, eur_per_month: 103,
    models: ['openai/gpt-6-astra', 'openai/gpt-5.6-sol', 'openai/gpt-5.6-terra'],
    coding_tool_fr: 'Codex, même enveloppe d’usage que le chat',
    url: 'https://learn.chatgpt.com/docs/pricing',
    fetched_fr: 'relevé le 14/09/2026',
    published_limits_fr: 'par période de 5 h : Astra 25 à 225 messages, Sol 50 à 500, Terra 125 à 1 000',
    capacity: NO_CAPACITY,
    caveat_fr: 'Grille régionale : le tarif diffère hors des États-Unis.',
  },

  // ——— Moonshot ——————————————————————————————————————————————————————————
  {
    id: 'moonshot/plus',
    vendor_fr: 'Moonshot AI', name_fr: 'Kimi Plus', usd_per_month: 19,
    models: ['moonshotai/kimi-k3'],
    coding_tool_fr: 'Kimi Code',
    url: 'https://www.kimi.com/membership/pricing',
    fetched_fr: 'relevé le 14/09/2026',
    published_limits_fr: null,
    capacity: NO_CAPACITY,
    caveat_fr: 'Aucun plafond chiffré publié.',
  },
  {
    id: 'moonshot/pro',
    vendor_fr: 'Moonshot AI', name_fr: 'Kimi Pro', usd_per_month: 39,
    models: ['moonshotai/kimi-k3'],
    coding_tool_fr: 'Kimi Code',
    url: 'https://www.kimi.com/membership/pricing',
    fetched_fr: 'relevé le 14/09/2026',
    published_limits_fr: null,
    capacity: NO_CAPACITY,
    caveat_fr: 'Aucun plafond chiffré publié.',
  },

  // ——— Alibaba ———————————————————————————————————————————————————————————
  {
    id: 'alibaba/token-personal-lite',
    vendor_fr: 'Alibaba Cloud', name_fr: 'Token Plan Personal — Lite', usd_per_month: 8,
    models: ['qwen/qwen3.8-max-0902'],
    coding_tool_fr: 'Qwen Code (CLI et greffons IDE)',
    url: 'https://www.alibabacloud.com/help/en/model-studio/token-plan-overview',
    fetched_fr: 'relevé le 14/09/2026',
    published_limits_fr: '2 500 Credits par 7 jours',
    capacity: NO_CAPACITY,
    caveat_fr: 'Ce n’est pas un abonnement grand public mais un forfait développeur acheté sur une '
             + 'console cloud, et il n’est ouvert qu’en région Singapour. Alibaba ne publie aucun '
             + 'barème de conversion de ses Credits, contrairement à Z.ai. Tarif catalogue ; une '
             + 'promo à 6 $ était affichée au relevé.',
  },
  {
    id: 'alibaba/token-personal-standard',
    vendor_fr: 'Alibaba Cloud', name_fr: 'Token Plan Personal — Standard', usd_per_month: 25,
    models: ['qwen/qwen3.8-max-0902'],
    coding_tool_fr: 'Qwen Code (CLI et greffons IDE)',
    url: 'https://www.alibabacloud.com/help/en/model-studio/token-plan-overview',
    fetched_fr: 'relevé le 14/09/2026',
    published_limits_fr: '10 000 Credits par 7 jours',
    capacity: NO_CAPACITY,
    caveat_fr: 'Forfait développeur, région Singapour uniquement. Credits non convertibles.',
  },
]

/** Completion record, measured. Separate from quality: a model can write well and not finish. */
export interface Reliability {
  /** Tasks carried to the end, out of those attempted. */
  finished: number
  attempted: number
  /** Interrupted by a quota, a limit or a crash — whatever the cause, the client bears it. */
  interruptions: number
  note_fr: string
}

/**
 * What our own runs observed. This is the axis a client who cannot afford a failure needs,
 * and no published leaderboard carries it: Astra holds the best published score in the grid
 * and the worst completion record in it.
 */
export const RELIABILITY: Record<string, Reliability> = {
  'anthropic/claude-opus-5': {
    finished: 9, attempted: 9, interruptions: 0,
    note_fr: 'Neuf tâches sur neuf, sans interruption.',
  },
  'x-ai/grok-4.6': {
    finished: 9, attempted: 9, interruptions: 0,
    note_fr: 'Neuf tâches sur neuf, sans interruption, sur un abonnement à 10 $.',
  },
  'z-ai/glm-5.3': {
    finished: 9, attempted: 9, interruptions: 0,
    note_fr: 'Neuf tâches sur neuf. Un premier run mal orchestré a été écarté avant celui-ci.',
  },
  'z-ai/glm-5.3-flash': {
    finished: 9, attempted: 9, interruptions: 1,
    note_fr: 'Neuf tâches sur neuf, mais une tâche tuée par le quota puis relancée depuis zéro.',
  },
  // The clean afternoon run. The morning attempt hung on a system dialog our machine raised,
  // not on the model, so it is not counted against it.
  'deepseek/deepseek-v4.1-flash': {
    finished: 9, attempted: 9, interruptions: 0,
    note_fr: 'Neuf tâches sur neuf, sans interruption, en raisonnement maximal.',
  },
  'openai/gpt-6-astra': {
    finished: 4, attempted: 9, interruptions: 4,
    note_fr: 'Quatre tâches sur neuf. Quatre sessions sur cinq tuées par le plafond en fin de '
           + 'course : les livrables existent, les durées sont tronquées.',
  },
  // Four tasks delivered over three runs. Both cuts came from the ChatGPT Plus ceilings, not
  // from the model: the client on that plan bears them all the same.
  'openai/gpt-5.6-sol': {
    finished: 4, attempted: 9, interruptions: 2,
    note_fr: 'Quatre tâches sur neuf menées au bout, et deux runs coupés par les plafonds de '
           + 'l’abonnement : la fenêtre de 5 h, puis la semaine, en pleine vérification finale.',
  },
}

export function reliabilityOf(modelId: string): Reliability | null {
  return RELIABILITY[modelId] ?? null
}

/** Plans that serve this model, cheapest first. */
export function plansFor(modelId: string): Plan[] {
  return PLANS.filter((plan) => plan.models.includes(modelId))
    .sort((a, b) => a.usd_per_month - b.usd_per_month)
}

/**
 * The cheapest plan serving this model, or null when no subscription reaches it. A model with
 * no plan cannot compete in subscription mode however cheap its API is — and the interface has
 * to say so, or a reader who saw it win on the API side will wonder where it went.
 */
export function cheapestPlanFor(modelId: string): Plan | null {
  return plansFor(modelId)[0] ?? null
}

/**
 * What share of a plan's period this project consumes, 0..1, or null when the plan's capacity
 * is not measured. Above 1 the project does not fit in one period.
 */
export function quotaShare(plan: Plan, apparentTokens: number): number | null {
  const capacity = plan.capacity.apparent_tokens
  if (capacity === null || capacity <= 0 || apparentTokens <= 0) return null
  return apparentTokens / capacity
}

/** The token split a project consumes, needed to price a credit ceiling. */
export interface TokenSplit {
  input_fresh: number
  cache_read: number
  output: number
}

/**
 * What a project costs in a plan's own published credits, for one model. Null when the plan
 * publishes no convertible ceiling, or publishes no multipliers for this model.
 *
 * This is the one place a ceiling can be priced without measuring it ourselves, and it is
 * also where a token ranking can invert: on our nine tasks GLM-5.3 spends 29 % FEWER tokens
 * than its Flash sibling and 2.2x MORE credits, because its multipliers are three times
 * higher. At a fixed subscription price, the plan's unit is the one that decides.
 */
export function creditsFor(plan: Plan, modelId: string, split: TokenSplit): number | null {
  const scheme = plan.capacity.credits
  const multipliers = scheme?.multipliers[modelId]
  if (!scheme || !multipliers) return null
  const [input, cached, output] = multipliers
  return (split.input_fresh * input + split.cache_read * cached + split.output * output)
    / scheme.divisor
}

/** Share of one period this project consumes, read through the plan's published credits. */
export function creditShare(plan: Plan, modelId: string, split: TokenSplit): number | null {
  const scheme = plan.capacity.credits
  const credits = creditsFor(plan, modelId, split)
  if (!scheme || credits === null || scheme.per_period <= 0) return null
  return credits / scheme.per_period
}

/** A weekly bucket refills this many times in the month the price pays for. */
export const WEEKS_PER_MONTH = 52 / 12

/** A plan, read against one project on one model. */
export interface PlanFit {
  plan: Plan
  /** Share of ONE period of the ceiling this project takes, or null when nobody can say. */
  share: number | null
  period: CapacityPeriod | null
  /** 'none' also when the plan publishes credits but no multipliers for this model. */
  basis: PlanCapacity['basis']
  /** false only when a known ceiling cannot hold the project within the month; null unknown. */
  fitsMonth: boolean | null
}

export function planFit(
  plan: Plan, modelId: string, split: TokenSplit, apparentTokens: number,
): PlanFit {
  const { capacity } = plan
  const measuredHere = capacity.measured_for === undefined
    || capacity.measured_for.includes(modelId)
  const share = capacity.basis === 'measured'
    ? (measuredHere ? quotaShare(plan, apparentTokens) : null)
    : capacity.basis === 'published' ? creditShare(plan, modelId, split)
      : null
  const periods = capacity.period === 'week' ? WEEKS_PER_MONTH : 1
  return {
    plan,
    share,
    period: share === null ? null : capacity.period,
    basis: share === null ? 'none' : capacity.basis,
    fitsMonth: share === null ? null : share <= periods,
  }
}

/**
 * The plan to quote for this project: the cheapest one not KNOWN to be too small.
 *
 * The price shown is the whole month, so a plan whose ceiling cannot hold the project in a
 * month is not its price — the next tier is. An unknown ceiling is given the benefit of the
 * doubt and flagged as unknown, never silently read as enough. When every known ceiling is
 * too small, the largest is returned with `fitsMonth: false`, so the interface can say it.
 */
export function fitPlan(
  modelId: string, split: TokenSplit, apparentTokens: number, plans: Plan[] = PLANS,
): PlanFit | null {
  const fits = plans.filter((plan) => plan.models.includes(modelId))
    .sort((a, b) => a.usd_per_month - b.usd_per_month)
    .map((plan) => planFit(plan, modelId, split, apparentTokens))
  return fits.find((fit) => fit.fitsMonth !== false) ?? fits[fits.length - 1] ?? null
}

/** How many projects of this size fit in one period. Null when the capacity is unmeasured. */
export function projectsPerPeriod(plan: Plan, apparentTokens: number): number | null {
  const share = quotaShare(plan, apparentTokens)
  return share === null ? null : 1 / share
}

export const SUBSCRIPTION_SOURCE = {
  label_fr: 'les pages tarifaires des éditeurs',
  measured_fr: 'Prix relevés sur la page de chaque éditeur, jamais saisis de mémoire.',
  caveat_fr: 'Contrairement aux tarifs à l’usage, aucun éditeur ne publie d’API de prix '
           + 'd’abonnement : ce sont des relevés datés, pas du direct. Les plafonds, eux, sont '
           + 'publiés dans des unités qui ne se comparent pas — la capacité affichée n’est '
           + 'donnée que lorsque nous l’avons mesurée sur un run réel, ou quand l’éditeur '
           + 'publie de quoi la calculer.',
}

export { NO_CAPACITY }
